import React, { useState } from "react";
import { useNavigate, useLocation, Navigate } from "react-router-dom";
import { Lock, User, Loader2 } from "lucide-react";
import { endpoints } from "@/lib/api";
import { setAuth, isAuthed } from "@/lib/auth";

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || "/";

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // Already signed in? Skip the form.
  if (isAuthed()) {
    return <Navigate to={from} replace />;
  }

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await endpoints.login(username.trim(), password);
      setAuth(res.token, res.username);
      navigate(from, { replace: true });
    } catch (err) {
      setError(
        err.response?.status === 401
          ? "Invalid username or password."
          : "Unable to sign in. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-100 px-4" data-testid="login-screen">
      <div className="w-full max-w-sm">
        <div className="flex justify-center mb-6">
          <img src={`${process.env.PUBLIC_URL}/ergoniq-logo.svg`} alt="Ergoniq" className="h-12 w-auto" />
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
          <h1 className="text-lg font-semibold text-slate-900 text-center">Dairy OIM</h1>
          <p className="text-xs text-slate-500 text-center mb-5">
            Operational &amp; Economic Intelligence
          </p>

          <form onSubmit={submit} className="space-y-3">
            <label className="block">
              <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Username</span>
              <div className="mt-1 flex items-center border border-slate-200 rounded-md px-2.5 focus-within:ring-2 focus-within:ring-slate-300">
                <User size={15} className="text-slate-400" />
                <input
                  type="text"
                  autoComplete="username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  required
                  data-testid="login-username"
                  className="w-full py-2 px-2 text-sm bg-transparent focus:outline-none"
                />
              </div>
            </label>

            <label className="block">
              <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Password</span>
              <div className="mt-1 flex items-center border border-slate-200 rounded-md px-2.5 focus-within:ring-2 focus-within:ring-slate-300">
                <Lock size={15} className="text-slate-400" />
                <input
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  data-testid="login-password"
                  className="w-full py-2 px-2 text-sm bg-transparent focus:outline-none"
                />
              </div>
            </label>

            {error && (
              <p className="text-xs text-red-600" data-testid="login-error">{error}</p>
            )}

            <button
              type="submit"
              disabled={loading}
              data-testid="login-submit"
              className="w-full flex items-center justify-center gap-2 bg-slate-900 text-white text-sm font-semibold rounded-md py-2.5 hover:bg-slate-800 disabled:opacity-60 transition-colors"
            >
              {loading && <Loader2 size={15} className="animate-spin" />}
              {loading ? "Signing in…" : "Sign in"}
            </button>
          </form>
        </div>
        <p className="text-[10px] text-slate-400 text-center mt-4">
          Ergoniq Dairy OIM · Authorized access only
        </p>
      </div>
    </div>
  );
}
